import isHotkey from 'is-hotkey';

export interface KeyboardControlledDexResultsStore {
    moveToNextSummaryOrResult: () => void;
    moveToPreviousSummaryOrResult: () => void;
    moveToNextQueryEntity: () => void;
    moveToPreviousQueryEntity: () => void;
    getSelectedResultId: () => number | undefined;
    getSelectedSummaryId: () => number | undefined;
}

export function useDexResultsKeyboardNavigationAndScroll(
    leftPanelListContainerRef: React.MutableRefObject<HTMLDivElement | null>,
    rightPanelScrollContainerRef: React.MutableRefObject<HTMLDivElement | null>,
    store: KeyboardControlledDexResultsStore,
    onKeyboardSelectsResult?: ({
        summaryId,
        resultId,
    }: {
        summaryId?: number;
        resultId?: number;
    }) => void
) {
    const focusAndScrollToSelectedItemElement = () => {
        let selectedResultElement;
        const selectedSummaryId = store.getSelectedSummaryId();
        const selectedResultId = store.getSelectedResultId();
        if (selectedSummaryId) {
            selectedResultElement = leftPanelListContainerRef?.current?.querySelector(
                `[data-summary-id="${selectedSummaryId}"]`
            );
        } else if (selectedResultId) {
            selectedResultElement = leftPanelListContainerRef?.current?.querySelector(
                `[data-result-id="${selectedResultId}"]`
            );
        }

        if (selectedResultElement) {
            (selectedResultElement as HTMLElement).focus?.();
            selectedResultElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    };

    const keyDownHandler: React.KeyboardEventHandler<HTMLDivElement> = (keyboardEvent) => {
        if (isHotkey('mod+Left', keyboardEvent)) {
            keyboardEvent.preventDefault();
            // store.moveTo* moves focus/selected result in the left panel list
            store.moveToPreviousQueryEntity();
            // onKeyboardSelectsResult sends request to mark selected result as read
            onKeyboardSelectsResult?.({
                summaryId: store.getSelectedSummaryId(),
                resultId: store.getSelectedResultId(),
            });
            // focus and scroll view to selected result
            focusAndScrollToSelectedItemElement();
            return;
        } else if (isHotkey('mod+Right', keyboardEvent)) {
            keyboardEvent.preventDefault();
            store.moveToNextQueryEntity();
            onKeyboardSelectsResult?.({
                summaryId: store.getSelectedSummaryId(),
                resultId: store.getSelectedResultId(),
            });
            focusAndScrollToSelectedItemElement();
            return;
        } else if (isHotkey('mod+Up', keyboardEvent) || isHotkey('mod+Down', keyboardEvent)) {
            keyboardEvent.preventDefault();
            return;
        }
        switch (keyboardEvent.key) {
            // Tab is intentionally not handled here: the browser walks the natural tab order
            // (each block is a tab stop via `tabIndex={0}` on the outer Card; individual
            // results stay non-tabbable). Use mod+Left/Right or ArrowLeft/Right to move
            // between blocks/results without leaving the panel.
            case 'ArrowLeft':
                store.moveToPreviousSummaryOrResult();
                onKeyboardSelectsResult?.({
                    summaryId: store.getSelectedSummaryId(),
                    resultId: store.getSelectedResultId(),
                });
                focusAndScrollToSelectedItemElement();
                break;
            case 'ArrowRight':
                store.moveToNextSummaryOrResult();
                onKeyboardSelectsResult?.({
                    summaryId: store.getSelectedSummaryId(),
                    resultId: store.getSelectedResultId(),
                });
                focusAndScrollToSelectedItemElement();
                break;
            case 'ArrowUp':
                keyboardEvent.preventDefault();
                if (rightPanelScrollContainerRef.current) {
                    rightPanelScrollContainerRef.current.scrollTop -= 50;
                }
                break;
            case 'ArrowDown':
                keyboardEvent.preventDefault();
                if (rightPanelScrollContainerRef.current) {
                    rightPanelScrollContainerRef.current.scrollTop += 50;
                }
                break;
        }
    };

    return { keyDownHandler, focusAndScrollToSelectedItemElement };
}
